import { Button, Form } from 'react-bootstrap';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BoundFormGroup from './boundForm/BoundFormGroup.js';
import PipelineEditor from 'screens/pipeline/PipelineEditor.js';
import ServiceInterestsEditor from './ServiceInterestsEditor.js';
import { action } from 'lib/actionHelpers';
import buildCreateCustomerPayload from './createLeadForm/lib/buildCreateCustomerPayload';
import createLeadFormDefaultValuesSelector from './createLeadForm/selectors/createLeadFormDefaultValuesSelector';
import formatAdditionalContactDetails from './createLeadForm/lib/formatAdditionalContactDetails.js';
import getContactDetailId from './createLeadForm/lib/getContactDetailId.js';
import { getLabel } from 'lib/labels';
import groupArray from '../lib/groupArray.js';
import { patchThreadMeta } from '../redux/actions/leadModalActions.js';
import { postLead } from '../redux/actions/leadsActions.js';
import selectors from '../selectors/selectors.js';
import setQuery from '../lib/setQuery.js';
import sortByProp from '../lib/sortByProp';
import useBoundForm from '../hooks/useBoundForm.js';
import SVG from '../images/svg/SVG.js';
import filterServicePersonsByLocationId from 'lib/filterServicePersonsByLocationId.js';
import { getInboxMessages } from 'screens/inbox/store/inboxScreenActions.js';
import { setPipelineAndLeadStatusOnCreation } from 'screens/inbox/lib/setPipelineAndLeadStatusOnCreation.js';

const CreateLeadForm = (props) => {
  const dispatch = useDispatch();

  const serviceProviderId = useSelector(selectors.serviceProviderId);
  const serviceProviderLocations = useSelector((state) => state.currentServiceProvider.locations);
  const serviceProviderPipelines = useSelector((state) => state.currentServiceProvider.pipelines);
  const servicePersons = useSelector(selectors.serviceProviderServicePersons);
  const activeNonLeadMessage = useSelector(selectors.activeNonLeadMessage);
  const currentUser = useSelector((state) => state.user);
  const utmSourceOptions = useSelector(selectors.utmSources).sort(sortByProp('name'));
  const defaultFormValues = useSelector(createLeadFormDefaultValuesSelector);
  const contactDetailTypes = useSelector((state) => state.app.contact_detail_types);
  const additionalFields = (useSelector(selectors.serviceProviderAdditionalFields) || {}).newCustomer || [];

  const [creatingLead, setCreatingLead] = useState(false);
  const [formChanged, setFormStateChanged] = useState(false);

  const form = useBoundForm({ initialState: defaultFormValues });

  const useTitle = additionalFields.find((n) => n.field === 'title' && n.category === 'contact');
  const useGender = additionalFields.find((n) => n.field === 'gender' && n.category === 'contact');
  const useBusinessName = additionalFields.find((n) => n.field === 'business_name' && n.category === 'address');
  const additionalContactDetails = formatAdditionalContactDetails(additionalFields, contactDetailTypes);
  const additionalContactDetailsGroups = groupArray(additionalContactDetails, 2);
  const pipelineOptions = [{ value: null, label: 'None' }, ...serviceProviderPipelines.map((n) => ({ value: n.id, label: n.pipeline_name }))];
  const utmMediumIsDisabled = !!(activeNonLeadMessage || {})[`utm_medium`];
  const utmSourceIsDisabled = !!(activeNonLeadMessage || {})[`utm_source`];
  const isNonLead = !!(activeNonLeadMessage || {}).id;
  const servicePersonOptions = servicePersons //
    .filter(filterServicePersonsByLocationId(form.state.service_location_id))
    .map((n) => ({ value: n.id, label: `${n.firstName || ''} ${n.lastName || ''}`.trim() }))
    .sort(sortByProp('label'));

  const pipelineStageOptions = ((serviceProviderPipelines.find((n) => n.id === form.state.pipeline_id) || {}).stages || [])
    .map((n) => ({ ...n, order: parseInt(n.order) }))
    .sort(sortByProp('order'))
    .map((n) => ({ value: n.id, label: n.pipeline_stage_name }));

  useEffect(() => {
    form.reset(defaultFormValues);
  }, [activeNonLeadMessage]);

  const handleCreateCustomer = (e) => {
    e.preventDefault();
    form.validate();
    if (!form.isValid) {
      return;
    }
    let payload = buildCreateCustomerPayload(form.state, {
      contactDetailTypes,
      activeNonLeadMessage,
      serviceProviderId,
      utmSources: utmSourceOptions,
      additionalContactDetails,
    });

    setCreatingLead(true);
    postLead(payload, (data, err) => {
      setCreatingLead(false);
      if (err) {
        let message = err.message || err;
        const type = String(message).includes('Phone') ? 'phone_number' : String(message).includes('Email') ? 'email_address' : undefined;
        const isDuplicate = String(message).includes('Duplicate');
        message = isDuplicate && type === 'phone_number' ? 'Phone number already exists.' : isDuplicate && type === 'email_address' ? 'Email already exists.' : message;
        form.invalidate(type, { message });
        return;
      }
      if (activeNonLeadMessage) {
        action('DELETE_MESSAGE_BY_DATA_SOURCE', {
          uniqueId: `${activeNonLeadMessage.id}_${activeNonLeadMessage.type}`,
          dataSource: 'inbox_',
        });

        action('DELETE_MESSAGES_BY_DATA_SOURCE', ['inbox_', 'leadModalChat']);
      }
      if (!props.modalView) {
        setPipelineAndLeadStatusOnCreation({ event: payload });
      }
      getInboxMessages({});
      const onClose = props.onClose || (() => {});
      setQuery({ lead: data.id, modal: 'true', eid: null, etype: null, type: null });
      onClose();
    });
  };

  const handleCancelClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const onClose = props.onClose || (() => {});
    onClose();
  };

  const handleThreadMetaSaveClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFormStateChanged(false);
    await dispatch(
      patchThreadMeta({
        serviceProviderId,
        eventType: activeNonLeadMessage.type,
        eventId: activeNonLeadMessage.id,
        firstName: form.state.first_name,
        lastName: form.state.last_name,
        phone: form.state.phone_number,
        email: form.state.email_address,
      })
    );
  };

  const handlePipelineEditorChange = (state) => {
    form.setState({ ...form.state, ...state });
  };

  const handleThreadMetaFieldChange = () => {
    setFormStateChanged(true);
  };

  const threadMetaFields = [
    [
      { id: 'first_name', placeholder: 'First Name', required: true, autoFocus: true },
      { id: 'last_name', placeholder: 'Last Name', required: true },
    ],
    [
      { id: 'phone_number', placeholder: 'Phone Number', format: 'phone', required: !form.state.email_address },
      { id: 'email_address', placeholder: 'Email Address', format: 'email', required: !form.state.phone_number },
    ],
  ];

  const basicInformationFields = [
    [
      { id: 'title', placeholder: 'Title', hidden: !useTitle },
      { id: 'gender', placeholder: 'Gender', options: 'gender', hidden: !useGender, nullable: true, nullableLabel: 'None' },
    ],
    ...additionalContactDetailsGroups.map((n) => n.map((o) => ({ id: getContactDetailId(o) || o.id || o.label, placeholder: o.label, format: o.format }))),
    [{ id: 'business_name', placeholder: 'Business Name', hidden: !(form.state.is_business || useBusinessName) }],
  ];

  const marketingInformationFields = [
    [
      { id: 'utm_medium', placeholder: 'Medium', options: utmMediumIsDisabled ? undefined : 'utmMedium', disabled: utmMediumIsDisabled, nullable: true, nullableLabel: 'None' },
      { id: 'utm_source', placeholder: 'Source', options: 'utmSource', disabled: utmSourceIsDisabled, freeform: true, nullable: true, nullableLabel: 'None' },
      { id: 'utm_campaign', placeholder: 'Campaign', options: 'utmCampaign', freeform: true, nullable: true, nullableLabel: 'None' },
    ],
  ];

  const servicePersonIdField = { formId: form.id, id: 'service_person_id', placeholder: 'Service Person', options: servicePersonOptions, hidden: servicePersons.length < 1 || !form.state.pipeline_id, nullable: true, nullableLabel: 'None' };
  const pipelineUserIdField = { formId: form.id, id: 'pipeline_user_id', placeholder: 'User', options: 'user', defaultValue: currentUser.id, hidden: !form.state.pipeline_id };
  const pipelineFields = [
    [
      { formId: form.id, id: 'pipeline_id', placeholder: 'Pipeline', options: pipelineOptions },
      { formId: form.id, id: 'pipeline_stage_id', placeholder: 'Stage', options: pipelineStageOptions, defaultValue: pipelineStageOptions[0], hidden: !form.state.pipeline_id },
    ],
    [{ formId: form.id, id: 'service_location_id', placeholder: 'Location', options: 'location', defaultValue: (serviceProviderLocations[0] || {}).id, hidden: !form.state.pipeline_id }, servicePersons.length < 1 ? pipelineUserIdField : servicePersonIdField],
    [servicePersons.length > 0 ? pipelineUserIdField : null],
  ];

  return (
    <div>
      <Form className="create-lead-form" onSubmit={handleCreateCustomer}>
        <div className="header">
          <div className="heading">New Customer</div>
          {!useBusinessName && (
            <div className="business-button">
              <Button className="" variant="secondary" data-title={form.state.is_business ? 'Remove Business Name' : 'Add Business Name'} onClick={(e) => form.setState({ ...form.state, is_business: !form.state.is_business })}>
                <SVG icon={'Business'} basic={false} />
                <span>{form.state.is_business ? 'Remove' : 'Add'}</span>
              </Button>
            </div>
          )}
        </div>
        <div className="subheading">Basic Information</div>
        <BoundFormGroup formId={form.id} fields={threadMetaFields} onChange={handleThreadMetaFieldChange} />
        {isNonLead && (
          <div className={`save-basic-row ${formChanged ? 'active' : ''}`}>
            <button className={'btn btn-primary'} onClick={handleThreadMetaSaveClick}>
              Save Basic Information
            </button>
          </div>
        )}
        <BoundFormGroup formId={form.id} fields={basicInformationFields} />
        <div className="subheading">Marketing Information</div>
        <BoundFormGroup formId={form.id} fields={marketingInformationFields} />
        <div className="subheading">Pipeline</div>
        <BoundFormGroup formId={form.id} fields={pipelineFields} />
        {!!form.state.pipeline_id && (
          <>
            <div className="col1">
              <PipelineEditor onChange={handlePipelineEditorChange} />
            </div>
            <div className="col1">
              <ServiceInterestsEditor onChange={(n) => form.set('service_interests', n)} value={form.state.service_interests} />
            </div>
          </>
        )}
        <div className={'button-row'}>
          <Button type={'submit'} variant="primary" disabled={creatingLead}>
            <SVG icon={'Add'} invert={true} />
            Create {getLabel('leads', false, true)}
          </Button>
          {props.onClose && (
            <Button type={'close'} variant="secondary" onClick={handleCancelClick}>
              Cancel
            </Button>
          )}
        </div>
      </Form>
    </div>
  );
};

export default CreateLeadForm;
